import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { humanizeString } from "@/lib/utils";
import { Alert } from "@/types";
import {
  AlertTriangle,
  Bell,
  BellOff,
  CheckCircle,
  Clock,
  X,
} from "lucide-react";

interface AlertsPanelProps {
  alerts: Alert[];
  showAcknowledged?: boolean;
}

export function AlertsPanel({ alerts }: AlertsPanelProps) {
  const filteredAlerts = alerts;
  //@ts-ignore
  const acknowledgeAlert = (alertId: string) => {
    // TODO
  };

  //@ts-ignore
  const dismissAlert = (alertId: string) => {
    // TODO
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case "critical":
        return <AlertTriangle className="h-4 w-4 text-red-400" />;
      case "high":
        return <AlertTriangle className="h-4 w-4 text-orange-400" />;
      case "medium":
        return <Clock className="h-4 w-4 text-yellow-400" />;
      default:
        return <Bell className="h-4 w-4 text-blue-400" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const colors = {
      critical: "text-red-400 bg-red-400/10 border-red-400/30",
      high: "text-orange-400 bg-orange-400/10 border-orange-400/30",
      medium: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
      low: "text-blue-400 bg-blue-400/10 border-blue-400/30",
    };

    return (
      <Badge
        className={`${
          colors[severity as keyof typeof colors]
        }  text-xs flex items-center gap-1`}
      >
        {getSeverityIcon(severity)}
        {humanizeString(severity)}
      </Badge>
    );
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor(
      (now.getTime() - time.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const unacknowledgedCount = alerts.filter(
    (alert) => !alert.acknowledged,
  ).length;

  return (
    <Card className="holo-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-cyan-400 ">
              <AlertTriangle className="h-5 w-5" />
              Alerts
              {unacknowledgedCount > 0 && (
                <Badge className="text-red-400 bg-red-400/10 border-red-400/30 ">
                  {unacknowledgedCount}
                </Badge>
              )}
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`p-4 rounded-lg border transition-all ${
                alert.acknowledged
                  ? "bg-slate-800/30 border-slate-700/50 opacity-60"
                  : "bg-slate-800/50 border-cyan-500/30 hover:border-cyan-500/50"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    {getSeverityBadge(alert.severity)}
                    <span className="text-xs text-slate-400 ">
                      {formatTimeAgo(alert.startsAt)}
                    </span>
                    {alert.deployId && (
                      <span className="text-xs px-2 py-1 bg-slate-700/50 rounded text-cyan-300 ">
                        {alert.deployId}
                      </span>
                    )}
                  </div>

                  <h4 className="font-medium text-cyan-100  mb-1">
                    {alert.name}
                  </h4>

                  <p className="text-sm text-slate-300  mb-2">
                    {alert.description}
                  </p>

                  {alert.acknowledged && (
                    <div className="flex items-center gap-2 text-xs text-slate-400 ">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      Acknowledged by {alert.acknowledgedByName}{" "}
                      {formatTimeAgo(alert.acknowledgedAt!)}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {!alert.acknowledged && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => acknowledgeAlert(alert.id)}
                      className="text-green-400 hover:text-green-300 hover:bg-green-500/10 "
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissAlert(alert.id)}
                    className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {filteredAlerts.length === 0 && (
            <div className="text-center py-8 text-slate-400 ">
              <BellOff className="h-12 w-12 mx-auto mb-4 opacity-50" />
              No alerts
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
